import { apiRequest, apiResponse } from "@/shared/api/client";
import { readSse } from "@/shared/api/sse";
import type { MediaInfoPayload, TorrentInfoPayload } from "@/features/download/api";
import { createSubscribe } from "@/features/subscribe/api";

export interface MediaSearchItem {
  source?: string;
  type?: string;
  title?: string;
  year?: string;
  title_year?: string;
  tmdb_id?: number;
  douban_id?: string;
  bangumi_id?: number;
  poster_path?: string;
  vote_average?: number;
  overview?: string;
  season?: number;
  number_of_seasons?: number;
  [key: string]: unknown;
}

export interface SearchContext {
  meta_info?: Record<string, unknown> | null;
  media_info?: MediaInfoPayload | null;
  torrent_info?: TorrentInfoPayload | null;
}

interface StreamEvent {
  type?: string;
  stage?: string;
  text?: string;
  items?: SearchContext[];
  total_items?: number;
}

/** 模糊搜索媒体元数据 */
export function searchMedia(title: string) {
  const q = encodeURIComponent(title.trim());
  return apiRequest<MediaSearchItem[]>(
    `/api/v1/media/search?title=${q}&type=media&page=1&count=20`,
  );
}

/** 按媒体 ID 精确搜种子（一次性） */
export function searchTorrentsByMedia(
  mediaId: string,
  opts?: { mtype?: string; season?: number },
) {
  const params = new URLSearchParams();
  if (opts?.mtype) params.set("mtype", opts.mtype);
  if (opts?.season != null) params.set("season", String(opts.season));
  const qs = params.toString();
  return apiResponse<SearchContext[]>(
    `/api/v1/search/media/${encodeURIComponent(mediaId)}${qs ? `?${qs}` : ""}`,
  );
}

/**
 * 渐进式搜种（SSE）。
 * 依赖登录时写入的 resource_token Cookie；失败时调用方可回退一次性接口。
 */
export async function searchTorrentsByMediaStream(
  mediaId: string,
  opts: {
    mtype?: string;
    season?: number;
    signal?: AbortSignal;
    onProgress?: (text: string) => void;
    onItems?: (items: SearchContext[], mode: "append" | "replace") => void;
  },
): Promise<SearchContext[]> {
  if (!/^(tmdb|douban|bangumi):[\w.-]+$/.test(mediaId)) {
    throw new Error("无效的媒体 ID");
  }
  const params = new URLSearchParams();
  if (opts.mtype) params.set("mtype", opts.mtype);
  if (opts.season != null) params.set("season", String(opts.season));
  const qs = params.toString();
  const url = `/api/v1/search/media/${encodeURIComponent(mediaId)}/stream${qs ? `?${qs}` : ""}`;

  let finalItems: SearchContext[] = [];

  await readSse(url, {
    signal: opts.signal,
    onEvent: (raw) => {
      const ev = raw as StreamEvent;
      if (ev.text) opts.onProgress?.(ev.text);
      if (ev.type === "append" && Array.isArray(ev.items)) {
        opts.onItems?.(ev.items, "append");
      }
      if (
        (ev.type === "replace" || ev.type === "done") &&
        Array.isArray(ev.items)
      ) {
        finalItems = ev.items;
        opts.onItems?.(ev.items, "replace");
      }
    },
  });

  return finalItems;
}

/** 从搜索结果发起订阅 */
export function subscribeFromMedia(media: MediaSearchItem) {
  return createSubscribe({
    name: media.title,
    year: media.year,
    type: media.type,
    tmdbid: media.tmdb_id,
    doubanid: media.douban_id,
    bangumiid: media.bangumi_id,
    season: media.season,
    poster: media.poster_path,
    vote: media.vote_average,
    description: media.overview,
  });
}
